import React, { useState } from "react";
import { useTranslation } from "react-i18next";

export default function VoltagesReportDialog({
    open,
    onClose,
    deviceId,
    deviceType,
    API_BASE,
    isVirtual = false,
}) {
    const { t, i18n } = useTranslation();
    const [reportType, setReportType] = useState("phase"); // phase | line | max_phase | max_line | min_phase | min_line
    const [format, setFormat] = useState("csv");
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    const handleDownload = async () => {
        try {
            setError("");
            if (!startDate || !endDate) {
                setError(t('reports.dateError'));
                return;
            }

            const start = new Date(startDate);
            const end = new Date(endDate);
            end.setHours(23, 59, 59, 999);

            if (end <= start) {
                setError(t('reports.endDateError'));
                return;
            }

            setLoading(true);

            const isExtreme = reportType.startsWith("max") || reportType.startsWith("min");
            const isHarmonics = reportType === "thd"; // ✅
            const voltageType = reportType.includes("line") ? "line" : "phase";
            const extremeType = reportType.startsWith("max")
                ? "max"
                : reportType.startsWith("min")
                    ? "min"
                    : null;

            let prefix = isVirtual ? "/virtual-reports" : "/reports"; // 🔹 ekledik
            let endpoint;

            if (isExtreme) {
                endpoint = `${API_BASE}${prefix}/export-extremes`;
            } else if (isHarmonics) {
                endpoint = `${API_BASE}${prefix}/export-voltage-harmonics`;
            } else {
                endpoint = `${API_BASE}${prefix}/export-voltages`;
            }


            const params = new URLSearchParams({
                start: start.toISOString(),
                end: end.toISOString(),
                format,
                lang: i18n.language, // Pass language to backend
            });

            // Sanal cihaz mı?
            if (isVirtual) {
                params.append("virtualId", deviceId);
            } else {
                params.append("deviceId", deviceId);
            }

            params.append("deviceType", deviceType);


            if (!isHarmonics && !isExtreme) {
                params.append("voltageType", voltageType);
            }
            if (extremeType) params.append("extremeType", extremeType);

            const url = `${endpoint}?${params.toString()}`;
            //console.log(url)

            if (format === "png") {
                const res = await fetch(url);
                if (!res.ok) throw new Error(t('reports.creationError'));
                const blob = await res.blob();
                const link = document.createElement("a");
                link.href = URL.createObjectURL(blob);
                link.download = `${reportType}_${Date.now()}.png`;
                link.click();
                URL.revokeObjectURL(link.href);
            } else {
                window.open(url, "_blank");
            }

            onClose();
        } catch (err) {
            console.error("Rapor indirme hatası:", err);
            setError(t('reports.downloadError'));
        } finally {
            setLoading(false);
        }
    };


    if (!open) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
            <div className="bg-white rounded-2xl shadow-xl p-6 w-80">
                <h2 className="text-lg font-semibold text-gray-800 mb-4">
                    {t('reports.options')}
                </h2>

                {/* ✅ Rapor Tipi */}
                <label className="block text-sm font-medium mb-1">{t('reports.type')}</label>
                <select
                    value={reportType}
                    onChange={(e) => setReportType(e.target.value)}
                    className="w-full border rounded-md p-2 mb-4"
                >
                    {/* Eğer cihaz multimetreyse sadece temel voltaj ve max/min seçenekleri göster */}
                    {deviceType === "multimeter" ? (
                        <>
                            <option value="phase">{t('reports.phaseVoltages')}</option>
                            <option value="line">{t('reports.lineVoltages')}</option>
                            <option value="max_phase">{t('reports.phaseMaxVoltages')}</option>
                            <option value="min_phase">{t('reports.phaseMinVoltages')}</option>
                            <option value="max_line">{t('reports.lineMaxVoltages')}</option>
                            <option value="min_line">{t('reports.lineMinVoltages')}</option>
                        </>
                    ) : deviceType === "gruparge" ? (
                        <option value="phase">{t('reports.phaseVoltages')}</option>
                    ) : (
                        <>
                            <option value="phase">{t('reports.phaseVoltages')}</option>
                            <option value="line">{t('reports.lineVoltages')}</option>

                            {/* ⚙️ Sadece sanal cihaz değilse Max/Min seçeneklerini göster */}
                            {!localStorage
                                .getItem("selectedDevice")
                                ?.includes('"mode":"virtual"') &&
                                deviceType !== "reactive" && (
                                    <>
                                        <option value="max_phase">{t('reports.phaseMaxVoltages')}</option>
                                        <option value="min_phase">{t('reports.phaseMinVoltages')}</option>
                                        <option value="max_line">{t('reports.lineMaxVoltages')}</option>
                                        <option value="min_line">{t('reports.lineMinVoltages')}</option>
                                    </>
                                )}

                            {/* THD sadece multimeter değilse gözüksün */}
                            <option value="thd">{t('reports.thd')}</option>
                        </>
                    )}
                </select>

                {/* Format */}
                <label className="block text-sm font-medium mb-1">{t('reports.format')}</label>
                <select
                    value={format}
                    onChange={(e) => setFormat(e.target.value)}
                    className="w-full border rounded-md p-2 mb-4"
                >
                    <option value="csv">CSV</option>
                    <option value="xlsx">{t('reports.excel')}</option>
                    <option value="png">{t('reports.chart')} (PNG)</option>
                </select>


                {/* Tarih Aralığı */}
                <label className="block text-sm font-medium mb-1">
                    {t('reports.startDate')}
                </label>
                <div className="flex gap-2 mb-3">
                    <input
                        type="date"
                        value={startDate.split("T")[0] || ""}
                        onChange={(e) => setStartDate(e.target.value + "T" + (startDate.split("T")[1] || "00:00"))}
                        className="flex-1 border rounded-md p-2"
                    />
                    <input
                        type="time"
                        value={startDate.split("T")[1] || ""}
                        onChange={(e) => setStartDate((startDate.split("T")[0] || "") + "T" + e.target.value)}
                        className="w-28 border rounded-md p-2"
                    />
                </div>

                <label className="block text-sm font-medium mb-1">{t('reports.endDate')}</label>
                <div className="flex gap-2 mb-4">
                    <input
                        type="date"
                        value={endDate.split("T")[0] || ""}
                        onChange={(e) => setEndDate(e.target.value + "T" + (endDate.split("T")[1] || "00:00"))}
                        className="flex-1 border rounded-md p-2"
                    />
                    <input
                        type="time"
                        value={endDate.split("T")[1] || ""}
                        onChange={(e) => setEndDate((endDate.split("T")[0] || "") + "T" + e.target.value)}
                        className="w-28 border rounded-md p-2"
                    />
                </div>

                {error && <p className="text-red-600 text-sm mb-2">{error}</p>}

                {loading ? (
                    <div className="flex justify-center py-3">
                        <div className="animate-spin h-6 w-6 border-4 border-blue-600 border-t-transparent rounded-full" />
                    </div>
                ) : (
                    <div className="flex justify-end gap-2">
                        <button
                            onClick={onClose}
                            className="px-3 py-2 rounded-md bg-gray-300 hover:bg-gray-400 text-sm"
                        >
                            {t('common.cancel')}
                        </button>
                        <button
                            onClick={handleDownload}
                            className="px-3 py-2 rounded-md bg-blue-600 text-white text-sm hover:bg-blue-700"
                        >
                            {t('common.download')}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}
